import React, { useState, useEffect } from 'react';
import './MenuPage.css';
import './DishDetailModal.css';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import MenuCategory from './MenuCategory';
import { useCart } from '../context/CartContext';

function MenuPage() {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState({});
  const [selectedDish, setSelectedDish] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { addToCart } = useCart();
  const [viewMode, setViewMode] = useState('delivery'); // 'delivery' or 'menu'

  // Fetch locations based on viewMode
  useEffect(() => {
    const fetchLocations = async () => {
      setLoadingLocations(true);
      const collectionName = viewMode === 'menu' ? 'allSedes' : 'menuSedes';
      const locationsCollection = collection(db, collectionName);
      const q = query(locationsCollection, orderBy('name')); // Reverted to orderBy('name')
      const locationsSnapshot = await getDocs(q);
      const locationsList = locationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      let finalSedes = locationsList;

      // Only apply schedule filtering for the 'delivery' mode which uses 'menuSedes'
      if (collectionName === 'menuSedes') {
        const isSedeOpen = (sede) => {
          if (sede.activationType === 'scheduled') {
            if (!sede.schedule) return false;

            const now = new Date();
            const days = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
            const currentDay = days[now.getDay()];
            const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');

            const daySchedule = sede.schedule[currentDay];
            if (!daySchedule || daySchedule.length === 0) {
              return false;
            }

            for (const slot of daySchedule) {
              if (currentTime >= slot.start && currentTime <= slot.end) {
                return true;
              }
            }
            return false;
          } else { // Defaults to manual
            return sede.visible;
          }
        };
        finalSedes = locationsList.filter(isSedeOpen);
      }

      setLocations(finalSedes);
      setLoadingLocations(false);
      setSelectedLocation(null); // Reset location on viewMode change
      setCategories([]);
      setMenuItems({});
    };

    fetchLocations();
  }, [viewMode]);

  // Fetch categories when a location is selected
  useEffect(() => {
    const fetchCategories = async () => {
      if (selectedLocation) {
        setLoadingCategories(true);
        // Categories are always fetched from menuSedes, as allSedes might not have the categories subcollection
        const categoriesCollection = collection(db, 'menuSedes', selectedLocation.id, 'categories');
        try {
          const categoriesSnapshot = await getDocs(categoriesCollection);
          const categoriesList = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setCategories(categoriesList);
        } catch (error) {
          console.error("Error fetching categories:", error);
          setCategories([]);
        }
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, [selectedLocation]);

  // Fetch menu items for each category
  useEffect(() => {
    const fetchMenuItems = async () => {
      if (categories.length > 0) {
        setLoadingItems(true);
        const newMenuItems = {};
        for (const category of categories) {
          const dishesCollection = collection(db, 'dishes');
          const q = query(
            dishesCollection,
            where('availableSedes', 'array-contains', selectedLocation.id),
            where('categoryName', '==', category.name),
            orderBy('name')
          );
          try {
            const dishesSnapshot = await getDocs(q);
            newMenuItems[category.name] = dishesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          } catch (error) {
            console.error(`Error fetching dishes for ${category.name}:`, error);
            newMenuItems[category.name] = [];
          }
        }
        setMenuItems(newMenuItems);
        setLoadingItems(false);
      }
    };
    fetchMenuItems();
  }, [categories, selectedLocation]);

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setCategories([]);
    setMenuItems({});
    setIsDropdownOpen(false);
  };

  const openModal = (dish) => {
    setSelectedDish(dish);
  };

  const closeModal = () => {
    setSelectedDish(null);
  };

  const handleAddToCart = (dish) => {
    addToCart(dish);
    closeModal();
  };

  return (
    <div className="menu-page">
      <div className="main-menu-banner"></div>

      <div className="menu-controls">
        <div className="view-mode-selector">
          <button onClick={() => setViewMode('delivery')} className={viewMode === 'delivery' ? 'active' : ''}>Domicilio</button>
          <button onClick={() => setViewMode('menu')} className={viewMode === 'menu' ? 'active' : ''}>Menú</button>
        </div>
        
        <div className="banner-location-selection">
          {loadingLocations ? (
            <p>Cargando sedes...</p>
          ) : (
            <div className="dropdown-container">
              <div className={`custom-dropdown ${isDropdownOpen ? 'open' : ''}`}>
                <div className="dropdown-selected" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                  {isDropdownOpen ? '' : (selectedLocation ? selectedLocation.name : 'Selecciona una Sede')}
                </div>
                {isDropdownOpen && (
                  <div className="dropdown-options">
                    {locations.map((location) => (
                      <div
                        key={location.id}
                        className="dropdown-option"
                        onClick={() => handleLocationSelect(location)}
                      >
                        {location.name}
                      </div>
                    ))}
                  </div>
                )}
                <select
                  className="location-dropdown-native"
                  onChange={(e) => handleLocationSelect(locations.find(loc => loc.id === e.target.value))}
                  value={selectedLocation?.id || ''}
                  style={{ display: 'none' }}
                >
                  <option value="" disabled>Selecciona una Sede</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {!selectedLocation && (
        <div className="menu-spacer"></div>
      )}

      {selectedLocation && (
        <div className="parallax-content-wrapper">
          <div className="menu-sections-container">
            {loadingCategories || loadingItems ? (
              <p>Cargando menú...</p>
            ) : (
              categories.length > 0 ? (
                <>
                  {categories.map(category => (
                    <MenuCategory
                      key={category.id}
                      category={category}
                      menuItems={menuItems[category.name]}
                      onDishClick={openModal}
                      selectedLocation={selectedLocation} // Pass selectedLocation
                    />
                  ))}
                </>
              ) : (
                <p>No hay categorías disponibles para esta sede.</p>
              )
            )}
            </div>
          </div>
        )}

      {selectedDish && (
        <div className="dish-detail-modal-overlay" onClick={closeModal}>
          <div className="dish-detail-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closeModal}>&times;</button>
            <h2>{selectedDish.name}</h2>
            <p className="modal-full-description">{selectedDish.description}</p>
            {selectedDish.imageUrl && <img src={selectedDish.imageUrl} alt={selectedDish.name} style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '4px', marginTop: '10px' }} />}
            {selectedLocation && selectedDish.prices && selectedDish.prices[selectedLocation.id] !== undefined ? (
              <p><strong>Precio:</strong> ${selectedDish.prices[selectedLocation.id]}</p>
            ) : (
              <p><strong>Precio:</strong> No disponible para esta sede</p>
            )}
            {viewMode === 'delivery' && (
              <button className="add-to-cart-btn" onClick={() => handleAddToCart(selectedDish)}>Agregar al Carrito</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuPage;
